/**
 * Note Top Bar Completed Component
 *
 * Top bar for completed notes (read-only with amendment capability)
 * - Left section: TOC button (xs/sm/md only)
 * - Right section: Copy actions + Amend/Edit button
 * - Menu: Copy actions (xs/sm), Copy/Paste Settings, Delete Note
 *
 * Used for both:
 * - Users in signing orgs who have signed notes
 * - Users in non-signing orgs who have completed notes
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ConfirmDialog } from '@components/primitives/ConfirmDialog';
import { getReadyToBillColor, getReadyToBillLabel } from '@features/billing/components/EncounterDetail/utils';
import { useJotBillAccess } from '@features/billing/hooks/useJotBillAccess';
import { billingApi, useCreateClaimMutation, useGetEncounterReadinessQuery } from '@features/billing/store/api';
import type { NoteSection } from '@features/notes';
import { useContainerQuery } from '@hooks/useContainerQuery';
import { DownloadIcon } from '@jotpsych/ui/icons/DownloadIcon';
import { EditIcon } from '@jotpsych/ui/icons/EditIcon';
import { JotBillIcon } from '@jotpsych/ui/icons/JotBillIcon';
import {
  AutoAwesome as AutoAwesomeIcon,
  ContentCopy as ContentCopyIcon,
  Delete as DeleteIcon,
  Deselect as DeselectIcon,
  EditNote as EditNoteIcon,
  HourglassEmpty as HourglassEmptyIcon,
  Settings as SettingsIcon,
  Verified as VerifiedIcon,
} from '@mui/icons-material';
import { Badge, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, TextField, Tooltip, Typography, useTheme } from '@mui/material';
import { useEventTracking } from '@services/statsig/hooks/useEventTracking';
import type { ClaimReference } from '@store/generated/encounters';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { useSnackbar } from 'notistack';

import { LAYOUT_SELECTORS } from '../constants/layout';
import type { CosignStatus } from '../hooks/useCosignContext';
import { clearCopySectionSelection, toggleChatVisibility } from '../store/chatSlice';
import { selectIsChatVisible } from '../store/selectors';

import type { MenuItemConfig } from './layouts/TopBarLayout';
import { TopBarLayout } from './layouts/TopBarLayout';
import { NoteTOCButton } from './NoteTOC/NoteTOCButton';
import { ClaimStatusChips } from './ClaimStatusChips';

export interface NoteTopBarCompletedProps {
  /**
   * Note ID for context
   */
  noteId: string;

  /**
   * Encounter ID for analytics context
   */
  encounterId?: string;

  /**
   * Whether the note was electronically signed
   */
  isSigned?: boolean;

  /**
   * Whether the org's note signing preference is enabled
   */
  hasNoteSigning?: boolean;

  /**
   * Whether note has a patient assigned
   */
  hasPatient?: boolean;

  /**
   * Handler for amending/editing the completed note
   */
  onAmendNote: () => void | Promise<void>;

  /**
   * Handler for deleting the note
   */
  onDeleteNote?: () => Promise<void>;

  /**
   * Whether note amendment is in progress
   */
  isAmending?: boolean;

  /**
   * Number of sections selected for copying
   */
  selectedSectionsCount?: number;

  /**
   * Array of note sections (for TOC button)
   */
  sections?: NoteSection[];

  /**
   * CSS selector for scroll container (for TOC button)
   * @default LAYOUT_SELECTORS.NOTE_SECTIONS_CONTAINER
   */
  containerSelector?: string;

  /**
   * Handler for copying all sections
   */
  onCopyAllSections?: () => void;

  /**
   * Handler for copying selected sections
   */
  onCopySelectedSections?: () => void;

  /**
   * Handler for opening Copy/Paste Settings dialog
   */
  onCopyPasteSettings?: () => void;

  /**
   * Handler for downloading signed PDF
   * Only shown when note is signed
   */
  onDownloadSignedPdf?: () => void;

  /**
   * Completion timestamp (Unix timestamp in seconds)
   * When the note was completed/signed
   */
  completionTimestamp?: number | null;

  /**
   * Patient ID for claim submission
   */
  patientId?: string | null;

  /**
   * Existing claims associated with this encounter
   */
  claims?: ClaimReference[] | null;

  /**
   * Whether JotBill is creating the claim automatically after note finalization
   */
  isAutoCreatingJotBillClaim?: boolean;

  /**
   * Co-sign workflow status
   */
  cosignStatus?: CosignStatus;

  /**
   * Whether the current user is the author of this note
   */
  isAuthor?: boolean;

  /**
   * Whether the current user is a supervisor
   */
  isSupervisor?: boolean;

  /**
   * Whether the current user has a billing admin role
   */
  isBillingAdmin?: boolean;

  /**
   * Handler for supervisor co-signing the note
   */
  onCosign?: () => void | Promise<void>;

  /**
   * Handler for supervisor requesting edits (with optional comment)
   */
  onRequestEdits?: (comment?: string) => void | Promise<void>;

  /**
   * Whether audit feature is enabled (controls sidebar toggle visibility)
   */
  isAuditEnabled?: boolean;

  /**
   * Number of audit issues to display as badge on sidebar toggle
   */
  auditIssueCount?: number;
}

/**
 * Top bar for completed notes
 * Shows amend/edit action with appropriate label based on signing status
 */
export const NoteTopBarCompleted: React.FC<NoteTopBarCompletedProps> = ({
  noteId,
  encounterId,
  isSigned = false,
  hasNoteSigning = false,
  hasPatient = false,
  onAmendNote,
  onDeleteNote,
  isAmending = false,
  selectedSectionsCount = 0,
  sections = [],
  containerSelector = LAYOUT_SELECTORS.NOTE_SECTIONS_CONTAINER,
  onCopyAllSections,
  onCopySelectedSections,
  onCopyPasteSettings,
  onDownloadSignedPdf,
  completionTimestamp: _completionTimestamp,
  patientId,
  claims,
  isAutoCreatingJotBillClaim = false,
  cosignStatus = 'none',
  isAuthor = true,
  isSupervisor = false,
  isBillingAdmin = false,
  onCosign,
  onRequestEdits,
  isAuditEnabled = false,
  auditIssueCount = 0,
}) => {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [requestEditsDialogOpen, setRequestEditsDialogOpen] = useState(false);
  const [requestEditsComment, setRequestEditsComment] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSubmittingClaim, setIsSubmittingClaim] = useState(false);
  const [createdClaim, setCreatedClaim] = useState<ClaimReference | null>(null);
  const { note: noteMetrics, noteSection } = useEventTracking();
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [createClaim] = useCreateClaimMutation();
  const { hasAccess: hasBillingClaims } = useJotBillAccess();
  const theme = useTheme();
  const isChatVisible = useAppSelector(selectIsChatVisible);

  const handleToggleSidebar = useCallback(() => {
    dispatch(toggleChatVisibility());
    noteMetrics.topbarChatToggleClicked(noteId, {
      chat_visible: !isChatVisible,
      encounter_id: encounterId,
    });
  }, [dispatch, noteMetrics, noteId, isChatVisible, encounterId]);

  // Delay readiness query by 1s after isSigned becomes true,
  // so the backend has time to register the signing before we check readiness
  const [readinessReady, setReadinessReady] = useState(false);
  useEffect(() => {
    if (!encounterId || !hasBillingClaims || !isSigned) {
      setReadinessReady(false);
      return;
    }
    const timer = setTimeout(() => setReadinessReady(true), 1000);
    return () => clearTimeout(timer);
  }, [encounterId, hasBillingClaims, isSigned]);

  // Fetch readiness signals for the encounter (lightweight endpoint)
  const { data: readiness } = useGetEncounterReadinessQuery(
    { encounterId: encounterId ?? '' },
    { skip: !readinessReady },
  );

  // Responsive breakpoints for container queries
  const { ref, breakpoint } = useContainerQuery<HTMLDivElement>({
    xs: { maxWidth: 480 },
    sm: { minWidth: 481, maxWidth: 768 },
    md: { minWidth: 769, maxWidth: 1024 },
    lg: { minWidth: 1025 },
  });

  // Show Copy button inline only on md and lg breakpoints, otherwise move to menu
  const showCopyButtonInline = breakpoint === 'md' || breakpoint === 'lg';

  // Show TOC button on xs/sm/md breakpoints (when inline TOC is hidden)
  const showTOCButton = breakpoint !== 'lg';

  const displayedClaims = useMemo(() => {
    if (!createdClaim) return claims ?? [];
    if (claims?.some(claim => claim.id === createdClaim.id)) return claims ?? [];
    return [...(claims ?? []), createdClaim];
  }, [claims, createdClaim]);
  const isClaimCreationPending = isSubmittingClaim || isAutoCreatingJotBillClaim;

  const handleDeleteClick = useCallback(() => {
    noteMetrics.topbarDeleteClicked(noteId, {
      view_mode: 'completed',
      is_signed: isSigned,
      encounter_id: encounterId,
    });
    setDeleteDialogOpen(true);
  }, [noteMetrics, noteId, isSigned, encounterId]);

  const handleMenuOpen = () => {
    noteMetrics.topbarMenuOpened(noteId, {
      view_mode: 'completed',
      encounter_id: encounterId,
    });
  };

  const handleDeselectAll = () => {
    noteSection.deselectAllClicked({
      source: 'topbar',
      selected_count: selectedSectionsCount,
      note_id: noteId,
      encounter_id: encounterId,
    });
    dispatch(clearCopySectionSelection());
  };

  const handleDeleteConfirm = async () => {
    if (!onDeleteNote) return;

    setIsDeleting(true);
    try {
      await onDeleteNote();
      setDeleteDialogOpen(false);
    } catch (error) {
      console.error('Failed to delete note:', error);
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  /**
   * Handle claim submission
   */
  const handleSubmitClaim = useCallback(async () => {
    if (!patientId || !encounterId || isSubmittingClaim) return;

    setIsSubmittingClaim(true);
    try {
      const claim = await createClaim({
        createClaimRequest: {
          encounter_ids: [encounterId],
          patient_id: patientId,
        },
      }).unwrap();

      if (claim.id) {
        setCreatedClaim({
          id: claim.id,
          status: claim.status ?? 'draft',
          claim_type: claim.claim_type ?? 'original',
          created_at: claim.created_at ?? null,
          insurance_payer_name: claim.insurance_payer_name ?? null,
          submitted_at: claim.submitted_at ?? null,
          total_charge_cents: claim.total_charge_cents,
        });
      }

      // Show success message and stay on the page
      enqueueSnackbar('Claim created successfully', { variant: 'success' });

      // Manually invalidate encounter cache so the new claim shows up
      dispatch(
        billingApi.util.invalidateTags([
          { type: 'Encounter' as const, id: encounterId },
          { type: 'BillingEncounter' as const, id: encounterId },
          { type: 'BillingEncountersList' as const, id: 'LIST' },
        ]),
      );
    } catch (error) {
      console.error('Failed to create claim:', error);
      const errorMessage =
        error && typeof error === 'object' && 'message' in error
          ? (error as Error).message
          : 'Failed to create claim';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setIsSubmittingClaim(false);
    }
  }, [patientId, encounterId, isSubmittingClaim, createClaim, enqueueSnackbar, dispatch]);

  /**
   * Determine appropriate button label based on signing status
   * - Signed notes with patient: "Amend"
   * - Unsigned notes or notes without signing capability: "Review & Edit"
   */
  const getButtonLabel = () => {
    if (hasNoteSigning && isSigned && hasPatient) {
      return 'Amend';
    }
    return 'Edit';
  };

  // Build menu items configuration
  const menuItems: MenuItemConfig[] = useMemo(() => {
    const items: MenuItemConfig[] = [];

    // Add Copy button to menu when not showing inline (xs/sm breakpoints)
    if (!showCopyButtonInline) {
      if (selectedSectionsCount === 0) {
        // Copy All Sections
        if (onCopyAllSections) {
          items.push({
            label: 'Copy All Sections',
            icon: <ContentCopyIcon fontSize="small" />,
            onClick: onCopyAllSections,
          });
        }
      } else {
        // Copy Selected
        if (onCopySelectedSections) {
          items.push({
            label: `Copy Selected (${selectedSectionsCount})`,
            icon: <ContentCopyIcon fontSize="small" />,
            onClick: onCopySelectedSections,
          });
        }
      }
    }

    // Add Download Signed Note (only when note is signed)
    if (isSigned && onDownloadSignedPdf) {
      items.push({
        label: 'Download Signed Note',
        icon: <DownloadIcon fontSize="small" />,
        onClick: () => {
          noteMetrics.topbarDownloadSignedPdfClicked(noteId, {
            encounter_id: encounterId,
          });
          onDownloadSignedPdf();
        },
      });
    }

    // Add Copy/Paste Settings
    items.push({
      label: 'Copy/Paste Settings',
      icon: <SettingsIcon fontSize="small" />,
      onClick: () => {
        noteMetrics.topbarCopyPasteSettingsClicked(noteId, {
          encounter_id: encounterId,
        });
        onCopyPasteSettings?.();
      },
      disabled: !onCopyPasteSettings,
    });

    // Add Delete Note (signed notes are immutable and cannot be deleted)
    items.push({
      label: 'Delete Note',
      icon: <DeleteIcon fontSize="small" />,
      onClick: handleDeleteClick,
      disabled: !onDeleteNote || isSigned,
      tooltip: isSigned ? "Signed notes can't be deleted" : undefined,
      divider: true,
    });

    return items;
  }, [
    showCopyButtonInline,
    selectedSectionsCount,
    onCopyAllSections,
    onCopySelectedSections,
    isSigned,
    onDownloadSignedPdf,
    onCopyPasteSettings,
    onDeleteNote,
    handleDeleteClick,
    noteMetrics,
    noteId,
    encounterId,
  ]);

  // Left section: TOC button
  const leftSection = (
    <>
      {/* TOC Button - shown on xs/sm/md when inline TOC is hidden */}
      {showTOCButton && sections.length > 0 && (
        <NoteTOCButton
          sections={sections}
          containerSelector={containerSelector}
          onCopySelected={onCopySelectedSections}
          noteId={noteId}
          encounterId={encounterId}
        />
      )}
    </>
  );

  // Right section: Copy actions + Amend/Edit button
  const rightSection = (
    <>
      {/* Dynamic Copy button - shown inline only on md/lg breakpoints */}
      {showCopyButtonInline &&
        (selectedSectionsCount === 0 ? (
          /* Copy All Sections - shown when nothing selected */
          onCopyAllSections && (
            <Button
              variant="text"
              onClick={onCopyAllSections}
              startIcon={<ContentCopyIcon />}
              sx={{
                textTransform: 'none',
                fontWeight: 500,
              }}
            >
              Copy All Sections
            </Button>
          )
        ) : (
          /* Deselect All + Copy Selected - shown when items selected */
          <>
            {/* Deselect All button - LEFT of Copy Selected */}
            <Button
              variant="text"
              onClick={handleDeselectAll}
              startIcon={<DeselectIcon />}
              sx={{
                textTransform: 'none',
                fontWeight: 500,
              }}
            >
              Deselect All
            </Button>

            {/* Copy Selected button */}
            {onCopySelectedSections && (
              <Button
                variant="text"
                onClick={onCopySelectedSections}
                startIcon={<ContentCopyIcon />}
                sx={{
                  textTransform: 'none',
                  fontWeight: 500,
                }}
              >
                Copy Selected ({selectedSectionsCount})
              </Button>
            )}
          </>
        ))}

      {/* View Claim button(s) - show when claims exist and not mid-creation */}
      {hasBillingClaims && !isClaimCreationPending && displayedClaims.length > 0 && (
        <ClaimStatusChips claims={displayedClaims} encounterId={encounterId} />
      )}

      {/* Create Claim button with readiness indicator */}
      {hasBillingClaims && (isClaimCreationPending || displayedClaims.length === 0) && isSigned && patientId && encounterId && (
        <Tooltip
          title={
            isAutoCreatingJotBillClaim
              ? 'Creating claim in JotBill'
              : readiness?.ready_to_bill_reasons && readiness.ready_to_bill_reasons.length > 0
                ? readiness.ready_to_bill_reasons.join(' • ')
                : getReadyToBillLabel(readiness?.ready_to_bill)
          }
          arrow
        >
          <Button
            variant="outlined"
            onClick={handleSubmitClaim}
            startIcon={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                {readiness?.ready_to_bill && (
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: getReadyToBillColor(readiness.ready_to_bill, theme),
                      flexShrink: 0,
                    }}
                  />
                )}
                <JotBillIcon fontSize="small" />
              </Box>
            }
            disabled={isClaimCreationPending}
            sx={{
              textTransform: 'none',
              fontWeight: 500,
            }}
          >
            {isAutoCreatingJotBillClaim
              ? 'Creating in JotBill...'
              : isSubmittingClaim
                ? 'Billing with JotBill...'
                : 'Bill with JotBill'}
          </Button>
        </Tooltip>
      )}

      {/* Co-sign workflow: reviewer actions for 'ready' notes */}
      {cosignStatus === 'ready' && !isAuthor && (isSupervisor || isBillingAdmin) ? (
        <>
          <Button
            variant="outlined"
            onClick={() => setRequestEditsDialogOpen(true)}
            startIcon={<EditNoteIcon />}
            sx={{ textTransform: 'none', fontWeight: 500 }}
          >
            Request Edits
          </Button>
          {/* Co-sign button: only for supervisors (billing admins can't co-sign) */}
          {isSupervisor && (
            <Button
              variant="contained"
              onClick={onCosign}
              startIcon={<VerifiedIcon />}
              disabled={isAmending}
              sx={{ textTransform: 'none', fontWeight: 500 }}
            >
              {isAmending ? 'Processing...' : 'Co-sign'}
            </Button>
          )}
        </>
      ) : cosignStatus === 'ready' && isAuthor ? (
        /* Author view: awaiting co-signature indicator + recall action.
         * The note sits in the supervisor's co-sign queue but is not yet
         * e-signed, so the author can recall it (back to 'reviewing') to
         * edit or regenerate from a different template, then re-sign to
         * resubmit for co-signature. */
        <>
          <Chip
            icon={<HourglassEmptyIcon sx={{ fontSize: 16 }} />}
            label="Awaiting Co-signature"
            color="warning"
            variant="outlined"
            sx={{ height: 32, fontWeight: 500 }}
          />
          <Tooltip
            title="Reopen this note for editing. It will leave your supervisor's co-sign queue until you sign again."
            arrow
          >
            <span>
              <Button
                variant="outlined"
                onClick={onAmendNote}
                startIcon={<EditIcon />}
                disabled={isAmending}
                sx={{ textTransform: 'none', fontWeight: 500 }}
              >
                {isAmending ? 'Processing...' : 'Recall & Edit'}
              </Button>
            </span>
          </Tooltip>
        </>
      ) : (
        /* Default: Amend/Edit button */
        <Button
          variant="contained"
          onClick={onAmendNote}
          startIcon={<EditIcon />}
          disabled={isAmending}
          sx={{
            textTransform: 'none',
            fontWeight: 500,
          }}
        >
          {isAmending ? 'Processing...' : getButtonLabel()}
        </Button>
      )}

      {/* Sidebar Toggle button - shown when audit is enabled */}
      {isAuditEnabled && (
        <Tooltip title={isChatVisible ? 'Hide Sidebar' : 'Show Sidebar'}>
          <Badge
            badgeContent={!isChatVisible && auditIssueCount > 0 ? auditIssueCount : 0}
            color="error"
            max={99}
            sx={{
              '& .MuiBadge-badge': {
                fontSize: '0.65rem',
                height: 18,
                minWidth: 18,
                padding: '0 4px',
              },
            }}
          >
            <IconButton
              onClick={handleToggleSidebar}
              size="medium"
              sx={{
                color: isChatVisible
                  ? theme.palette.primary.contrastText
                  : theme.palette.primary.main,
                bgcolor: isChatVisible ? theme.palette.primary.main : 'transparent',
                transition: 'background-color 0.2s ease, color 0.2s ease',
                '&:hover': {
                  bgcolor: isChatVisible ? theme.palette.primary.dark : theme.palette.action.hover,
                },
              }}
            >
              <AutoAwesomeIcon />
            </IconButton>
          </Badge>
        </Tooltip>
      )}
    </>
  );

  return (
    <>
      <TopBarLayout
        ref={ref}
        left={leftSection}
        right={rightSection}
        menuItems={menuItems}
        onMenuOpen={handleMenuOpen}
      />

      {/* Request Edits Dialog */}
      <Dialog
        open={requestEditsDialogOpen}
        onClose={() => {
          setRequestEditsDialogOpen(false);
          setRequestEditsComment('');
        }}
        fullWidth
       maxWidth={"sm"}>
        <DialogTitle>Request Edits</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            The author will be notified and can make changes before resubmitting.
          </Typography>
          <TextField
            autoFocus
            label="Comment (optional)"
            placeholder="Describe what changes are needed..."
            multiline
            rows={3}
            fullWidth
            value={requestEditsComment}
            onChange={(e) => setRequestEditsComment(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setRequestEditsDialogOpen(false);
              setRequestEditsComment('');
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              onRequestEdits?.(requestEditsComment || undefined);
              setRequestEditsDialogOpen(false);
              setRequestEditsComment('');
            }}
          >
            Request Edits
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        variant="warning"
        title="Delete Note?"
        message="This action cannot be undone. Are you sure you want to delete this note?"
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        confirmText="Delete"
        loading={isDeleting}
      />
    </>
  );
};
